import React from "react";
import clsx from "clsx";
import s from "./StaffBonusDynamicTable.less";
import { useTranslation } from "react-i18next";
import { IDynamicTable } from "~/app/type/common";

interface IDynamicTableProps {
  table: IDynamicTable;
}

const DynamicTable = (props: IDynamicTableProps) => {
  const { t } = useTranslation();
  const { columns, rows, footers } = (props ?? {}).table;
  const getDepth = (cols) => {
    return cols?.length
      ? 1 + Math.max(...cols.map((col) => getDepth(col.subColumns || [])))
      : 0;
  };
  const getMaxDepth = (cols, depth = 1) => {
    return cols.reduce((max, col) => {
      if (col.subColumns && col.subColumns.length > 0) {
        return Math.max(max, getMaxDepth(col.subColumns, depth + 1));
      }
      return Math.max(max, depth);
    }, depth);
  };

  const getLeafColumnsCount = (column) => {
    if (column.colSpan) return column.colSpan;
    if (!column.subColumns || column.subColumns.length === 0) return 1;
    return column.subColumns.reduce(
      (sum, col) => sum + getLeafColumnsCount(col),
      0
    );
  };

  const maxDepth = getDepth(columns);

  const buildHeaderRows = (columns) => {
    const maxDepth = getMaxDepth(columns);
    const rows = Array.from({ length: maxDepth }, () => []);

    const traverse = (cols, depth = 0) => {
      cols.forEach((col) => {
        const hasSub = col.subColumns && col.subColumns.length > 0;
        const colSpan = col.colSpan ?? (hasSub ? getLeafColumnsCount(col) : 1);
        const rowSpan = col.rowSpan ?? (hasSub ? 1 : maxDepth - depth);

        rows[depth].push({
          label: col.label,
          colSpan,
          rowSpan,
          isLeaf: !hasSub,
          code: col.code ?? null,
        });

        if (hasSub) {
          traverse(col.subColumns, depth + 1);
        }
      });
    };

    traverse(columns);

    const hasCodes = rows[maxDepth - 1].some((cell) => cell.code);

    if (hasCodes) {
      const codeRow = rows[maxDepth - 1].map((cell) => ({
        label: cell.code ?? "",
        colSpan: 1,
        rowSpan: 1,
        isCodeRow: true,
      }));
      rows.push(codeRow);
    }

    return rows;
  };

  const countLeafColumns = (cols) => {
    return cols.reduce((acc, col) => {
      return (
        acc + (col.subColumns?.length ? countLeafColumns(col.subColumns) : 1)
      );
    }, 0);
  };

  const buildCodeRow = (cols, acc = []) => {
    cols.forEach((col) => {
      if (col.subColumns?.length) {
        buildCodeRow(col.subColumns, acc);
      } else {
        acc.push(col.code || "");
      }
    });
    return acc;
  };

  const getAllKeys = (cols, acc = []) => {
    cols.forEach((col) => {
      if (col.subColumns?.length) {
        getAllKeys(col.subColumns, acc);
      } else if (col.key) {
        acc.push(col.key);
      }
    });
    return acc;
  };

  if (!columns || !rows || rows.length === 0) {
    return (
      <div className={s.dynamicTable}>
        <div className={s.noData}>{t("staff.noData")}</div>
      </div>
    );
  }

  // const rows = buildRows(headers);
  const headerRows = buildHeaderRows(columns);
  const codeRow = buildCodeRow(columns);
  const allKeys = getAllKeys(columns);

  const colorMap = {
    red: s.red,
    green: s.green,
    orange: s.orange,
  };

  const rowSpanTracker = {};

  return (
    <div className={s.dynamicTable}>
      <table>
        <thead>
          {headerRows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <th
                  key={j}
                  colSpan={cell.colSpan}
                  rowSpan={cell.rowSpan}
                  className={clsx(
                    s.headerCell,
                    cell.isCodeRow && s.codeRowCell
                  )}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {allKeys.map((key, cellIndex) => {
                const rowSpanKey = `${key}-${rowIndex}`;
                if (rowSpanTracker[rowSpanKey]) return null;

                const rowSpan = row?.data?.[key + "_rowSpan"] || 1;
                if (rowSpan > 1) {
                  for (let i = 1; i < rowSpan; i++) {
                    rowSpanTracker[`${key}-${rowIndex + i}`] = true;
                  }
                }

                const cellData = row.data[key];
                const isObject =
                  cellData &&
                  typeof cellData === "object" &&
                  "value" in cellData;

                const displayValue = isObject
                  ? cellData.value === "moratorium"
                    ? "м"
                    : cellData.value
                  : cellData === "moratorium"
                  ? "м"
                  : cellData || "-";

                const cellStyle =
                  isObject && cellData.style
                    ? { backgroundColor: cellData.style.bgColor }
                    : {};
                const link = isObject && cellData.style?.link;
                const EmployeProfile = (link: string) => () =>
                  window.open(link || "", "_blank");
                return (
                  <td
                    key={`${rowIndex}-${cellIndex}`}
                    rowSpan={rowSpan > 1 ? rowSpan : undefined}
                    style={cellStyle}
                  >
                    {link ? (
                      <a onClick={EmployeProfile(link)} target={"_blank"}>
                        {displayValue}
                      </a>
                    ) : (
                      displayValue
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {!!(footers ?? []).length && (
          <tfoot>
            {(footers ?? []).map((footerItem, rowIndex) => (
              <tr key={rowIndex}>
                {allKeys.map((key) => {
                  const footerItemMap = new Map(
                    footerItem.map((fi) => [fi.key, fi])
                  );

                  const cellData = footerItemMap.get(key);
                  const isObject =
                    cellData &&
                    typeof cellData === "object" &&
                    "value" in cellData;

                  const cellStyle =
                    isObject && cellData.style
                      ? { backgroundColor: cellData.style.bgColor }
                      : {};
                  const link = isObject && cellData.style?.link;
                  const EmployeProfile = (link: string) => () =>
                    window.open(link || "", "_blank");
                  return (
                    <td key={`${rowIndex}-${key}`} style={cellStyle}>
                      {link ? (
                        <a onClick={EmployeProfile(link)} target={"_blank"}>
                          {footerItemMap.get(key)?.value}
                        </a>
                      ) : (
                        footerItemMap.get(key)?.value
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default DynamicTable;
